import { Button, Divider } from "antd";
import { FilterValue } from "antd/lib/table/interface";
import { Key, useMemo } from "react";
import { TagButton } from "./TagButton";
import TagList, { TagType } from "./TagList";
import styles from "./TableFiltersSection.module.scss";

interface TableFiltersSectionProps<KeyType extends string> {
  tags?: TagType<KeyType>[];
  selectedCount: number;
  onFilterClick?: () => void;
  displaySelectedOnly: boolean;
  clearTags?: (tableId?: string) => void;
  removeTag?: (key: KeyType, value: boolean | Key, tableId?: string) => void;
  currentItems: number;
  groupedItems?: number;
  totalItems: number;
  onSelectAll?: () => void;
  showSelectAll?: boolean;
  tableId?: string;
}

export type FiltersType = Record<string, FilterValue | null>;

export function TableFiltersSection<KeyType extends string>({
  tags,
  onFilterClick,
  selectedCount,
  displaySelectedOnly,
  clearTags,
  removeTag,
  currentItems,
  groupedItems,
  totalItems,
  onSelectAll,
  showSelectAll,
  tableId,
}: TableFiltersSectionProps<KeyType>) {
  const itemsString = useMemo(() => {
    const itemsToUse = groupedItems ?? totalItems;
    return currentItems === itemsToUse
      ? itemsToUse
      : `${currentItems} / ${itemsToUse}`;
  }, [currentItems, groupedItems, totalItems]);

  return (
    <div className={styles.host} data-cy="datalist-tags">
      <span>{itemsString} items</span>
      <Divider type="vertical" className={styles.divider} />
      <strong>Filters</strong>
      <TagButton
        disabled={!selectedCount}
        onClick={onFilterClick}
        text={`${selectedCount} selected`}
        tooltipText="Click to show the selected rows only"
        selected={displaySelectedOnly}
      />
      {tags && clearTags && removeTag && (
        <TagList<KeyType>
          onClear={clearTags}
          onRemove={removeTag}
          tags={tags}
          tableId={tableId}
        />
      )}
      {onSelectAll && showSelectAll && (
        <Button className={styles.selectAll} type="link" onClick={onSelectAll}>
          {selectedCount === totalItems ? "Deselect All" : "Select All"}
        </Button>
      )}
    </div>
  );
}
